/*
 * Copyright (c) 2022. World Wide Generation Ltd
 */

import { DataShareCreateBasic } from '../../types/dataShare';
import { FormGroup, Input, Label } from 'reactstrap';
import { getDataScopeSurvey, getScopeModules, mergeScopes } from '@utils/dataShare';
import { PACK, SURVEY } from '@constants/terminology';
import { getRequestScopeExtended } from '@utils/requestScope';
import { CustomSelectOption, SelectPacks } from '@features/select-packs';
import { RequestScope } from '@actions/api';
import { useMemo } from 'react';
import { MetricGroup } from '@g17eco/types/metricGroup';
import { SelectFactory, SelectTypes } from '@g17eco/molecules/select/SelectFactory';
import { SimpleTooltip } from '@g17eco/molecules/simple-tooltip';
import { DataScopeAccess, DataShareScopeView } from '@g17eco/types/surveyDataScope';

const TOOLTIP = {
  accessModules:
    `Access is only granted for metrics that have been marked as public and ${SURVEY.PLURAL} that have been marked as complete by the organisation`,
  accessViews: 'Select how you would like to view this organisations data',
};

interface TextCounterProps {
  text: string | undefined;
  limit?: number;
}

const RemainingCharactersCounter = ({ text, limit = 255 }: TextCounterProps) => {
  return (
    <div className={'text-right'}>
      <span className={'text-ThemeTextPlaceholder'}>{limit - (text?.length ?? 0)} characters left</span>
    </div>
  );
};

interface DataShareOption {
  label: JSX.Element | string;
  value: DataShareScopeView;
}

const dataShareScopeViewsOptions: DataShareOption[] = [
  {
    label: `${SURVEY.CAPITALIZED_SINGULAR} overview page`,
    value: DataShareScopeView.Survey,
  },
  {
    label: 'Insights page',
    value: DataShareScopeView.Insights,
  },
  {
    label: 'Sustainability report downloads',
    value: DataShareScopeView.Downloads,
  },
];

export const shareScopeViews = dataShareScopeViewsOptions.map(({ value }) => value);

type FormFields = Pick<DataShareCreateBasic, 'title' | 'content' | 'dataScope'>;

interface DataShareFormParams {
  form: FormFields;
  onChange: (value: Partial<DataShareCreateBasic>) => void;
  disabled?: boolean;
  disabledContent?: boolean;
  titleMax?: number;
  contentMax?: number;
  customMetricGroups?: MetricGroup[];
  restrictedScopes?: string[];
}

export const DataShareForm = (props: DataShareFormParams) => {
  const {
    form,
    onChange,
    contentMax = 255,
    disabled,
    disabledContent,
    customMetricGroups = [],
    restrictedScopes = [],
  } = props;

  // NOTE: Old logic: scope = undefined -> allow to share all scope modules
  const { scope, views = [], access } = form.dataScope?.survey ?? {};

  const groupOptions = customMetricGroups.reduce((acc, cur) => {
    acc[cur._id] = { name: `CMP: ${cur.groupName}`, src: cur.groupData?.icon };
    return acc;
  }, {} as CustomSelectOption);

  const allRequestScopes = useMemo(() => getRequestScopeExtended(groupOptions), [groupOptions]);
  const selectedScopes = getScopeModules(form.dataScope).filter((module) => allRequestScopes.some(s => s.code === module));

  const handleChangeAllScopes = (checked: boolean) => {
    const newAccess = checked ? DataScopeAccess.Full : DataScopeAccess.Partial;
    return onChange({ dataScope: { survey: { ...getDataScopeSurvey(form.dataScope), access: newAccess } } });
  };

  const handleChangeModules = (updateScopes: RequestScope[]) => {
    const removeScopes = allRequestScopes.filter(
      (item) => selectedScopes.includes(item.code) && updateScopes.every((s) => s.code !== item.code)
    );
    const addScopes = allRequestScopes.filter(
      (item) => !selectedScopes.includes(item.code) && updateScopes.some((s) => s.code === item.code)
    );

    const mergeRemoveScopes = mergeScopes(scope ?? {}, removeScopes, 'remove');
    const mergeAddScopes = mergeScopes(mergeRemoveScopes, addScopes, 'add');

    return onChange({ dataScope: { survey: { ...getDataScopeSurvey(form.dataScope), scope: mergeAddScopes } } });
  };

  const handleChangeAllScopeViews = (checked: boolean) => {
    const newScopeViews = checked
      ? Array.from(new Set([...views, ...shareScopeViews]))
      : views.filter((v) => !shareScopeViews.includes(v));
    return onChange({ dataScope: { survey: { ...getDataScopeSurvey(form.dataScope), views: newScopeViews } } });
  };

  const changeScopeView = (view: DataShareScopeView) => {
    const newScopeViews = !views.includes(view) ? [...views, view] : views.filter((v) => v !== view);
    return onChange({ dataScope: { survey: { ...getDataScopeSurvey(form.dataScope), views: newScopeViews } } });
  };

  const handleChangeViews = (selectedViews: string[]) => {
    const hasMassaging = views.includes(DataShareScopeView.Messaging);
    const newScopeViews = [...selectedViews, ...(hasMassaging ? [DataShareScopeView.Messaging] : [])];
    return onChange({
      dataScope: { survey: { ...getDataScopeSurvey(form.dataScope), views: newScopeViews as DataShareScopeView[] } },
    });
  };

  const selectedScopeViews = dataShareScopeViewsOptions.filter((o) => views.includes(o.value));
  const isSharedAllScopeViews = selectedScopeViews.length === dataShareScopeViewsOptions.length;

  return (
    <div className='d-flex'>
      <fieldset className={'w-100'} disabled={disabled}>
        <div className='row'>
          <div className='col'>
            <Label className='mb-3'>
              <span className='strong'>Subject: </span>
              {form.title}
            </Label>
            <div className='mb-3'>
              <Input
                rows={6}
                type={'textarea'}
                className='fs-6 px-3 py-2'
                maxLength={contentMax}
                required
                value={form.content}
                onChange={(e) => onChange({ content: e.target.value })}
                disabled={disabled || disabledContent}
              />
              <RemainingCharactersCounter text={form.content} limit={contentMax} />
            </div>
          </div>
        </div>

        <div className='mt-3'>
          <FormGroup switch className='d-flex'>
            <Input
              id={`access-all-survey-${PACK.PLURAL}`}
              type='switch'
              className='mr-2'
              checked={access === DataScopeAccess.Full}
              onChange={(e) => handleChangeAllScopes(e.currentTarget.checked)}
            />
            <label htmlFor={`access-all-survey-${PACK.PLURAL}`} className='text-md'>
              Data access to all {SURVEY.ADJECTIVE} {PACK.PLURAL}
              <SimpleTooltip text={TOOLTIP.accessModules}>
                <i className='fa-light fa fa-circle-info text-ThemeIconSecondary ml-2 fs-5' />
              </SimpleTooltip>
            </label>
          </FormGroup>
          <div className='mt-2'>
            <SelectPacks
              placeholder={`${SURVEY.CAPITALIZED_ADJECTIVE} ${PACK.SINGULAR} access`}
              selectedScopes={selectedScopes}
              groupOptions={groupOptions}
              handleChangePacks={handleChangeModules}
              isDisabled={access === DataScopeAccess.Full}
              disabledOptions={restrictedScopes}
            />
          </div>
        </div>

        <div className='mt-4'>
          <FormGroup switch className='d-flex'>
            <Input
              id='access-all-scope-views'
              type='switch'
              className='mr-2'
              checked={isSharedAllScopeViews}
              onChange={(e) => handleChangeAllScopeViews(e.currentTarget.checked)}
            />
            <label htmlFor='access-all-scope-views' className='text-md'>
              Access all data views
              <SimpleTooltip text={TOOLTIP.accessViews}>
                <i className='fa-light fa fa-circle-info text-ThemeIconSecondary ml-2 fs-5' />
              </SimpleTooltip>
            </label>
          </FormGroup>
          <div className='mt-2'>
            <SelectFactory
              selectType={SelectTypes.MultipleSelect}
              placeholder='Data view'
              options={dataShareScopeViewsOptions}
              onChange={handleChangeViews}
              isDisabled={isSharedAllScopeViews}
              values={selectedScopeViews.map((v) => v.value)}
              showSelectAll={false}
            />
          </div>
        </div>

        <FormGroup switch className='d-flex mt-4'>
          <Input
            id={'access-messaging'}
            type='switch'
            className='mr-2'
            checked={views.includes(DataShareScopeView.Messaging)}
            onChange={() => changeScopeView(DataShareScopeView.Messaging)}
          />
          <label htmlFor={'access-messaging'} className='text-md'>
            Messaging (Sent & Receive)
          </label>
        </FormGroup>
      </fieldset>
    </div>
  );
};
